import { Badge } from 'corso-admin-ui';
import { Pegasus } from 'corso-types';
import DescriptionList, { Description } from '~/components/DescriptionList';
import { useProductGroups } from '~/features/settings/hooks/useProductGroups';
import ProductGroupProducts from '~/features/settings/productGroups/ProductGroupProducts';
import {
  advancedExchangeKindLabel,
  advancedExchangeLabel,
  advancedExchangeSortCriteriaLabel,
} from '~/utils/enumNameMaps';

export default function DisplayEventOfferAdvancedExchange({
  params,
}: {
  params: Pegasus.EventOfferAdvancedExchange['params'];
}) {
  const { exchangeKind } = params;
  const { data } = useProductGroups();

  if (exchangeKind === 'variant') {
    const variantDescriptions: Description[] = [
      { term: 'Exchange For', details: advancedExchangeKindLabel.variant },
      {
        term: 'Exchange Method',
        details: advancedExchangeLabel[params.advancedExchangeMethod],
      },
    ];

    if (params.minInventoryAvailableToCustomer) {
      variantDescriptions.push({
        term: 'Minimum Inventory Available to Customer',
        details: params.minInventoryAvailableToCustomer,
      });
    }

    return (
      <div className="mt-2">
        <DescriptionList layoutType="grid" descriptions={variantDescriptions} />
      </div>
    );
  }

  const {
    storeProductGroupIds,
    storeProductTags,
    minInventoryAvailableToCustomer,
    advancedExchangeMethod,
    isNoAdjustmentVariantExchangeEnabled,
  } = params;

  const productGroups = data.filter((group) =>
    storeProductGroupIds.includes(group.id),
  );

  const configDescriptions: Description[] = [];

  configDescriptions.push({
    term: 'Exchange Method',
    details: advancedExchangeLabel[advancedExchangeMethod],
  });

  if (minInventoryAvailableToCustomer) {
    configDescriptions.push({
      term: 'Minimum Inventory Available to Customer',
      details: minInventoryAvailableToCustomer,
    });
  }

  configDescriptions.push({
    term: 'Filter and Search Enabled',
    details: params.isAdvancedExchangeFilterAndSearchEnabled ? 'Yes' : 'No',
  });

  configDescriptions.push({
    term: 'Excluded Option Names',
    details: params.excludedAdvancedExchangeOptionNames.join(', '),
  });

  configDescriptions.push({
    term: 'Allow Variant Exchange Without Adjustment',
    details: isNoAdjustmentVariantExchangeEnabled ? 'Yes' : 'No',
  });

  configDescriptions.push({
    term: 'Order Products By',
    details:
      advancedExchangeSortCriteriaLabel[params.advancedExchangeSortCriteria],
  });

  return (
    <div className="mt-2 flex flex-col gap-4">
      <div>
        <p className="text-sm font-medium text-neutral-700">Exchange For</p>
        <div className="mt-1 flex flex-col gap-2">
          {productGroups.map((group) => (
            <div className="flex items-center gap-2" key={group.id}>
              <Badge>{group.name}</Badge>
              <ProductGroupProducts group={group} key={group.id} />
            </div>
          ))}
        </div>
      </div>

      {storeProductTags && storeProductTags.length > 0 && (
        <div>
          <p className="text-sm font-medium text-neutral-700">Product Tags</p>
          <div className="mt-1 flex flex-wrap gap-2">
            {storeProductTags.map((tag) => (
              <Badge key={tag}>{tag}</Badge>
            ))}
          </div>
        </div>
      )}

      {configDescriptions.length > 0 && (
        <DescriptionList layoutType="grid" descriptions={configDescriptions} />
      )}
    </div>
  );
}
